import { isActiveChatStatus } from "@/constants/chat-statuses";
import { ERROR_MESSAGES } from "@/constants/error-messages";
import {
  RAG_LOADING_MESSAGES,
  RAG_LOADING_MESSAGES_INTERVAL,
} from "@/constants/rag-loading-messages";
import { MAX_MESSAGE_LENGTH } from "@/constants/search-constants";
import type { ChatManagerResult } from "@/hooks/use-chat-manager";
import { useLimitedInput } from "@/hooks/use-limited-input";
import { CHAT_MODE, useChatState } from "@/store/chat-state";
import { useWidgetRuntimeConfig } from "@/context/widget-runtime-config";
import styled from "@emotion/styled";
import { type ComponentType, type FC, type JSX, useEffect } from "react";
import type { ExtraProps } from "react-markdown";
import ReactMarkdown from "react-markdown";
import { colors, hideScrollbar, media, spacing } from "../design-tokens";
import { AnimatedSection } from "../primitives/animated-section";
import { LoadingIterator } from "../primitives/loading-iterator";
import { QueryInput } from "../primitives/query-input";
import {
  Blockquote,
  BodyListItem,
  BodyOrderedList,
  BodyUnorderedList,
  NewTabBodyLink,
  ParagraphRegular,
} from "../primitives/typographies";
import { WidgetHeader } from "../primitives/widget-header";
import { ActionsGrid } from "./actions-grid";
import { CollapsedResponseContainer } from "./collapsed-response-container";
import { SourcesButton } from "./sources-button";
import { SuggestionCards } from "./suggestion-cards";
import { UserFeedbackComponent } from "./user-feedback";
import { safeUrlTransform } from "@/utils/url-helpers";
import { DisclaimerRow } from "./disclaimer-row";

const WidgetContainer = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${spacing["4xl"]}px;
  box-sizing: border-box;
  width: 100%;
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${spacing.md}px;
  height: 100%;
  width: 100%;
  min-height: 100px;
`;

const ResponseContainer = styled.div<{ $isModal: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${spacing["2xl"]}px;
  overflow-y: auto;
  ${({ $isModal }) => !$isModal && hideScrollbar}
`;

type Components = {
  [Key in keyof JSX.IntrinsicElements]?:
    | ComponentType<JSX.IntrinsicElements[Key] & ExtraProps>
    | keyof JSX.IntrinsicElements;
};

const StyledParagraphRegular = styled(ParagraphRegular)`
  &:not(:first-child) {
    margin-top: ${spacing.md}px;
  }
`;

const components: Components = {
  p: StyledParagraphRegular,
  a: NewTabBodyLink,
  ul: BodyUnorderedList,
  ol: BodyOrderedList,
  li: BodyListItem,
  blockquote: Blockquote,
};

const ResponseContent = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: ${colors.text};

  & > *:first-child {
    margin-top: 0;
  }

  & > *:last-child {
    margin-bottom: 0;
  }
`;

const VerticalSeparator = styled.div`
  width: 1px;
  height: 16px;
  background-color: ${colors.neutralGrey};
  margin: 0 ${spacing.sm}px;

  ${media.sm} {
    margin: 0 ${spacing["2xl"]}px;
  }
`;

type ChatContentProps = ChatManagerResult & {
  isModal?: boolean;
  isSearchPage?: boolean;
  onCloseClick?: () => void;
  onSourcesClick?: () => void;
};

export const ChatContent: FC<ChatContentProps> = ({
  userQuery,
  status,
  response,
  mode,
  error: chatError,
  isModal = false,
  isSearchPage = false,
  onCloseClick,
  onSourcesClick,
}) => {
  const {
    showChatQuestions,
    widgetSuggestedQuestions,
    widgetPlaceholderText,
  } = useWidgetRuntimeConfig();
  const {
    value,
    isValid,
    error: inputError,
    setValue,
  } = useLimitedInput("", {
    maxLength: MAX_MESSAGE_LENGTH,
    showError: true,
  });
  const {
    submitQuery,
    setContextUrl,
    setUserQuery,
    setMode,
  } = useChatState();

  useEffect(() => {
    if (userQuery) {
      setValue(userQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { content, helpfulNextSteps, traceId, observationId, sources } =
    response ?? {};

  const handleSubmit = (query: string) => {
    if (!isValid) {
      return;
    }
    // Clear contextUrl when asking a regular question
    setContextUrl(undefined);
    submitQuery(query);
    setValue(query);
  };

  const handleSummariseThisPage = () => {
    const currentUrl = window.location.href;
    if (currentUrl) {
      // Set the context URL for summarization
      setContextUrl(currentUrl);
      // Use a clean title for the UI
      setUserQuery("Page Summary");
      // Set mode to INPUT to trigger the chat flow
      setMode(CHAT_MODE.INPUT);
      // Since we're already in the modal, no need to open it again
    }
  };

  const handleSuggestionClick = (query: string) => {
    // Check if this is the special "Summarise this page" suggestion
    if (query === "SUMMARISE_THIS_PAGE") {
      handleSummariseThisPage();
    } else {
      handleSubmit(query);
    }
  };

  return (
    <WidgetContainer>
      <WidgetHeader
        title={isSearchPage && userQuery ? userQuery : "How can we help today?"}
        onCloseClick={onCloseClick}
        showCloseButton={isModal}
      />

      {!isSearchPage && (
        <QueryInput
          value={value}
          onChange={setValue}
          onSubmit={handleSubmit}
          placeholder={widgetPlaceholderText}
          autoFocus={isModal && mode === CHAT_MODE.INPUT && !userQuery}
          error={inputError}
        />
      )}

      <ContentArea>
        {mode === CHAT_MODE.ANSWERING && (
          <CollapsedResponseContainer
            shouldCollapseInitially={isSearchPage && status !== "submitted"}
          >
            <ResponseContainer $isModal={isModal}>
              <ResponseContent>
                {chatError && status === "error" && (
                  <ReactMarkdown components={components}>
                    {ERROR_MESSAGES.GENERIC_CHAT_ERROR}
                  </ReactMarkdown>
                )}
                {(isActiveChatStatus(status) || status === "ready") && content && !chatError && (
                  <ReactMarkdown
                    components={components}
                    urlTransform={safeUrlTransform}
                  >
                    {content}
                  </ReactMarkdown>
                )}
                {isActiveChatStatus(status) && !content && !chatError && (
                  <LoadingIterator
                    messages={RAG_LOADING_MESSAGES}
                    interval={RAG_LOADING_MESSAGES_INTERVAL}
                  />
                )}
              </ResponseContent>
              <AnimatedSection
                isVisible={status === "ready"}
                animationType="fadeSlide"
                gap="2xl"
              >
                {helpfulNextSteps && helpfulNextSteps.length > 0 && <ActionsGrid actions={helpfulNextSteps} />}
                <UserFeedbackComponent
                  traceId={traceId}
                  observationId={observationId}
                >
                  {sources && sources.length > 0 && onSourcesClick && (
                    <>
                      <VerticalSeparator />
                      <SourcesButton
                        sources={sources}
                        onSourcesClick={onSourcesClick}
                      />
                    </>
                  )}
                </UserFeedbackComponent>
              </AnimatedSection>
            </ResponseContainer>
          </CollapsedResponseContainer>
        )}
        {mode === CHAT_MODE.INPUT && (
          <SuggestionCards
            suggestions={
              showChatQuestions
                ? widgetSuggestedQuestions?.map(question => ({ question })) ?? []
                : []
            }
            onSuggestionClick={handleSuggestionClick}
          />
        )}
      </ContentArea>
      {!isModal && <DisclaimerRow />}
    </WidgetContainer>
  );
};
